import { type ModelCreator, BaseModel, BaseModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import modalImage from 'img/quota-landing/family/spaceToEach/modalImg.png';
import React from 'react';
import { IS_MOBILE_BROWSER, IS_WEBVIEW } from 'reactApp/appHelpers/configHelpers';
import {
    isFeatureSpaceToEachQuotaLandingPromo,
    MAIL_SPACE_MODAL_ID,
    spaceToEachModalEnable,
    spaceToEachModalShowIntervalLimit,
} from 'reactApp/appHelpers/featuresHelpers/features/familySpaceToEach';
import { getFamilyAlbumsFeature, getFamilyFoldersFeature } from 'reactApp/modules/family/family.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { isCloudOrMailOrApiOverQuota } from 'reactApp/modules/userQuota/overquota.helpers';
import { FAMILY_SPACE_TO_EACH_WELCOME_ITEM_ID } from 'reactApp/sections/QuotaLanding/FamilySpaceToEachWelcomeTooltip/FamilySpaceToEachWelcomeTooltip';
import { store as reduxStore } from 'reactApp/store';
import { sendXray } from 'reactApp/utils/ga';

import { SUMMER_PROMO_2026_MODAL_QUERY_PARAM } from './summerCampaign2026ModalCreator';

export const HIDE_SPACE_TO_EACH_QUERY_PARAM = 'hideSpaceToEach';
class PopupModel extends BaseModel<BaseModelProps> {
    getParent(): HTMLElement | null {
        return document.documentElement;
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/SpaceToEachQuotaLandingModal/SpaceToEachQuotaLandingModal'));

const SuspendedComponent = () => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent />
        </React.Suspense>
    );
};

export const multiTariffSpaceToEach: ModelCreator = ({ models, render, stores }) =>
    new PopupModel(
        MAIL_SPACE_MODAL_ID,
        models.base,
        new BaseModelProps(MAIL_SPACE_MODAL_ID, {
            store: new LocalStorageStore(MAIL_SPACE_MODAL_ID, stores.localStorage),
            renderer: new ReactRenderer(MAIL_SPACE_MODAL_ID, SuspendedComponent, render.react),
            templateData: {},
            ignoreTestOnRedraw: true,
            showLimit: 1000,
            closeLimit: 1000,
            ignorePromoShowInterval: true,
            showInterval: spaceToEachModalShowIntervalLimit,
            preloadingImages: [modalImage],
            test: (resolve, reject) => {
                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);

                const onReject = () => {
                    reject();
                    sessionStorage.setItem(`${FAMILY_SPACE_TO_EACH_WELCOME_ITEM_ID}-show`, 'true');
                };

                const onResolve = () => {
                    resolve();
                    sessionStorage.removeItem(`${FAMILY_SPACE_TO_EACH_WELCOME_ITEM_ID}-show`);
                };

                if (storage !== EStorageType.quotaCleaner) {
                    onReject();
                    return;
                }

                const queryParams = SettingsSelectors.getQueryParams(state);
                const { action = '' } = queryParams;
                const searchParams = new URLSearchParams(window.location.search);
                if (action || searchParams.get(HIDE_SPACE_TO_EACH_QUERY_PARAM)) {
                    onReject();
                    sendXray(['promo-test', MAIL_SPACE_MODAL_ID, 'rejectbyaction']);
                    return;
                }

                // Не показываем поверх акционной модалки летней акции, открытой по прямой ссылке
                if (searchParams.get(SUMMER_PROMO_2026_MODAL_QUERY_PARAM)) {
                    onReject();
                    return;
                }

                const isBizUser = UserSelectors.isBizUser(state);
                const isCorpUser = UserSelectors.isCorpUser(state);
                const isOverquota = isCloudOrMailOrApiOverQuota(state);
                const hasFamilyFoldersFeature = getFamilyFoldersFeature(state);
                const hasFamilyAlbumsFeature = getFamilyAlbumsFeature(state);
                const isFamilyUser = hasFamilyFoldersFeature && hasFamilyAlbumsFeature;

                const isReject =
                    !spaceToEachModalEnable ||
                    !isFeatureSpaceToEachQuotaLandingPromo ||
                    IS_WEBVIEW ||
                    IS_MOBILE_BROWSER ||
                    isBizUser ||
                    isCorpUser ||
                    isOverquota ||
                    isFamilyUser;

                if (isReject) {
                    onReject();
                    return;
                }

                onResolve();
            },
        })
    );
